import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BrowseByCategoryWithFilters from './components/BrowseByCategoryWithFilters';
import MenuGrid from './components/MenuGrid';
import Icon from '../../components/AppIcon';

const MenuCatalog = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [filters, setFilters] = useState({
    dietary: '',
    priceRange: '',
    sortBy: 'name'
  });
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(3);

  // Mock categories data
  const categories = [
  {
    id: 'all',
    name: 'All Services',
    icon: 'Grid3X3',
    count: 48,
    featured: false
  },
  {
    id: 'express',
    name: 'Express Delivery',
    icon: 'Zap',
    count: 12,
    featured: true
  },
  {
    id: 'freight',
    name: 'Freight Services',
    icon: 'Truck',
    count: 18,
    featured: false
  },
  {
    id: 'air',
    name: 'Air Cargo',
    icon: 'Plane',
    count: 10,
    featured: false
  },
  {
    id: 'ocean',
    name: 'Ocean Shipping',
    icon: 'Ship',
    count: 8,
    featured: true
  }];


  // Mock shipping services data
  const menuItems = [
  {
    id: 1,
    name: "Express International Air",
    subtitle: "Priority air freight for urgent shipments",
    description: "Fast and reliable international air cargo service with door-to-door delivery. Perfect for time-sensitive shipments requiring expedited customs clearance and priority handling.",
    price: 299.99,
    originalPrice: 349.99,
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    imageAlt: "Commercial cargo airplane on airport tarmac ready for international express air freight delivery",
    category: 'express',
    dietary: ['insured', 'tracked'],
    tags: ['signature', 'premium', '24-48hrs'],
    featured: true,
    rating: 4.8,
    reviewCount: 124,
    prepTime: 48,
    createdAt: '2024-10-10'
  },
  {
    id: 2,
    name: "Ocean Freight Container",
    subtitle: "Full container load for bulk shipping",
    description: "Cost-effective ocean freight service for large shipments. Full container load (FCL) with secure handling and comprehensive tracking from port to port.",
    price: 1499.99,
    image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec",
    imageAlt: "Large cargo container ship loaded with colorful shipping containers sailing through ocean",
    category: 'ocean',
    dietary: ['insured', 'eco-friendly'],
    tags: ['bulk', 'economical', '15-30days'],
    featured: false,
    rating: 4.6,
    reviewCount: 89,
    prepTime: 720,
    createdAt: '2024-10-08'
  },
  {
    id: 3,
    name: "Same-Day Local Delivery",
    subtitle: "Ultra-fast delivery within city limits",
    description: "Express same-day delivery service for urgent local shipments. Pickup within 2 hours and delivery the same business day with real-time tracking.",
    price: 49.99,
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
    imageAlt: "Delivery courier in uniform holding package for same-day express local delivery service",
    category: 'express',
    dietary: ['tracked'],
    tags: ['urgent', 'local', 'same-day'],
    featured: true,
    rating: 4.4,
    reviewCount: 156,
    prepTime: 8,
    createdAt: '2024-10-12'
  },
  {
    id: 4,
    name: "Standard Ground Freight",
    subtitle: "Reliable overland shipping solution",
    description: "Economical ground freight service for domestic shipments. Reliable delivery with full tracking and insurance coverage for peace of mind.",
    price: 89.99,
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7",
    imageAlt: "Modern freight truck on highway transporting cargo for ground shipping delivery",
    category: 'freight',
    dietary: ['insured'],
    tags: ['domestic', 'reliable', '3-5days'],
    featured: false,
    rating: 4.7,
    reviewCount: 203,
    prepTime: 120,
    createdAt: '2024-10-05'
  },
  {
    id: 5,
    name: "Warehousing & Distribution",
    subtitle: "Secure storage with fulfillment",
    description: "Professional warehousing services with inventory management and order fulfillment. Climate-controlled facilities with 24/7 security and real-time inventory tracking.",
    price: 199.99,
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c",
    imageAlt: "Modern warehouse interior with organized shelving systems and inventory management",
    category: 'freight',
    dietary: ['secure', 'climate-controlled'],
    tags: ['storage', 'fulfillment', 'monthly'],
    featured: true,
    rating: 4.3,
    reviewCount: 67,
    prepTime: 24,
    createdAt: '2024-10-11'
  },
  {
    id: 6,
    name: "International Express Parcel",
    subtitle: "Fast worldwide parcel delivery",
    description: "Premium international parcel service with express customs clearance. Door-to-door delivery to over 220 countries with full tracking and insurance.",
    price: 149.99,
    image: "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55",
    imageAlt: "International shipping packages with customs labels ready for express worldwide delivery",
    category: 'express',
    dietary: ['insured', 'tracked'],
    tags: ['worldwide', 'customs', '3-5days'],
    featured: false,
    rating: 4.5,
    reviewCount: 91,
    prepTime: 96,
    createdAt: '2024-10-09'
  },
  {
    id: 7,
    name: "Pallet Shipping Service",
    subtitle: "LTL freight for pallet loads",
    description: "Less-than-truckload (LTL) freight service for pallet shipments. Cost-effective solution for medium-sized loads with professional handling and tracking.",
    price: 249.99,
    image: "https://images.unsplash.com/photo-1605732562742-3023a888e56e",
    imageAlt: "Wooden pallets loaded with boxes in warehouse ready for LTL freight shipping",
    category: 'freight',
    dietary: ['insured'],
    tags: ['LTL', 'pallets', '5-7days'],
    featured: false,
    rating: 4.2,
    reviewCount: 134,
    prepTime: 144,
    createdAt: '2024-10-07'
  },
  {
    id: 8,
    name: "White Glove Delivery",
    subtitle: "Premium handling for delicate items",
    description: "Specialized white glove delivery service for fragile and high-value items. Includes unpacking, assembly, and debris removal with expert care.",
    price: 399.99,
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf",
    imageAlt: "Professional movers in white gloves carefully handling delicate furniture for premium delivery",
    category: 'express',
    dietary: ['insured', 'premium'],
    tags: ['fragile', 'premium', 'assembly'],
    featured: true,
    rating: 4.9,
    reviewCount: 287,
    prepTime: 72,
    createdAt: '2024-10-13'
  },
  {
    id: 9,
    name: "Refrigerated Transport",
    subtitle: "Temperature-controlled shipping",
    description: "Specialized refrigerated transport for temperature-sensitive cargo. Maintains precise temperature control throughout the entire shipping journey.",
    price: 449.99,
    image: "https://images.unsplash.com/photo-1601362840469-51e4d8d58785",
    imageAlt: "Refrigerated cargo truck with temperature control unit for cold chain logistics",
    category: 'freight',
    dietary: ['climate-controlled', 'monitored'],
    tags: ['cold-chain', 'perishable', 'monitored'],
    featured: false,
    rating: 4.1,
    reviewCount: 45,
    prepTime: 96,
    createdAt: '2024-10-06'
  },
  {
    id: 10,
    name: "Customs Brokerage Service",
    subtitle: "Expert customs clearance assistance",
    description: "Professional customs brokerage service to handle all import/export documentation and clearance. Ensures smooth passage through customs with compliance expertise.",
    price: 179.99,
    image: "https://images.unsplash.com/photo-1565008576549-57569a49371d",
    imageAlt: "Customs officer reviewing international shipping documents for clearance processing",
    category: 'freight',
    dietary: ['documentation'],
    tags: ['customs', 'compliance', 'paperwork'],
    featured: false,
    rating: 4.4,
    reviewCount: 112,
    prepTime: 48,
    createdAt: '2024-10-04'
  },
  {
    id: 11,
    name: "Hazmat Shipping",
    subtitle: "Certified dangerous goods transport",
    description: "Specialized shipping service for hazardous materials with full compliance and certification. Expert handling of dangerous goods with proper documentation.",
    price: 599.99,
    image: "https://images.unsplash.com/photo-1611348524140-53c9a25263d6",
    imageAlt: "Hazmat shipping containers with warning labels for dangerous goods transportation",
    category: 'freight',
    dietary: ['certified', 'compliant'],
    tags: ['hazmat', 'certified', 'specialized'],
    featured: false,
    rating: 4.3,
    reviewCount: 178,
    prepTime: 120,
    createdAt: '2024-10-03'
  },
  {
    id: 12,
    name: "Overnight Express",
    subtitle: "Next-day delivery guarantee",
    description: "Guaranteed overnight delivery service for urgent shipments. Pickup by 6 PM for next business day delivery with real-time tracking and signature confirmation.",
    price: 129.99,
    image: "https://images.unsplash.com/photo-1605792657660-596af9009e82",
    imageAlt: "Express delivery van at night for overnight shipping service with urgent packages",
    category: 'express',
    dietary: ['tracked', 'guaranteed'],
    tags: ['overnight', 'guaranteed', 'urgent'],
    featured: true,
    rating: 4.8,
    reviewCount: 342,
    prepTime: 24,
    createdAt: '2024-10-14'
  }];


  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddToCart = async (item) => {
    // Simulate adding to cart
    console.log('Adding to cart:', item);
    setCartCount((prev) => prev + 1);

    // Show success feedback (you could add a toast notification here)
    return new Promise((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    if (action === 'login') navigate('/login');
    if (action === 'register') navigate('/register');
    if (action === 'account') navigate('/user-account');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header
        cartCount={cartCount}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onSearch={() => {}} // Add missing onSearch prop
        onLogout={() => {}} />

      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-primary text-primary-foreground py-6 lg:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl lg:text-4xl font-heading font-bold mb-3">
                Our Menu
              </h1>
              <p className="text-base lg:text-lg font-body opacity-90 max-w-2xl mx-auto">
                Discover our carefully crafted dishes made with the finest ingredients. From appetizers to desserts, every item tells a story of flavor and passion.
              </p>
            </div>
          </div>
        </section>

        {/* Menu Content */}
        <section className="py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {/* Unified Browse by Category with Filters & Sort */}
              <BrowseByCategoryWithFilters
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
                onFiltersChange={handleFiltersChange}
              />

              {/* Menu Grid */}
              <MenuGrid
                items={menuItems}
                loading={loading}
                onAddToCart={handleAddToCart}
                activeCategory={activeCategory}
                filters={filters} />

            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="bg-muted py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-card rounded-2xl p-8 lg:p-12 shadow-warm">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Phone" size={24} color="white" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4">
                Can't Find What You're Looking For?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Our chefs are happy to accommodate special requests and dietary restrictions. Give us a call to discuss custom menu options or ask about today's specials.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+15551234567"
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-body font-medium hover:bg-primary/90 transition-all duration-200 hover:scale-105">

                  <Icon name="Phone" size={18} />
                  <span>Call (555) 123-4567</span>
                </a>
                <button
                  onClick={() => navigate('/shopping-cart')}
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-accent text-accent-foreground rounded-lg font-body font-medium hover:bg-accent/90 transition-all duration-200 hover:scale-105">

                  <Icon name="ShoppingCart" size={18} />
                  <span>View Cart ({cartCount})</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="UtensilsCrossed" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">Pesto</h3>
                <p className="text-xs opacity-80 -mt-1">Restaurant</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm opacity-80">
                © {new Date()?.getFullYear()} Pesto Restaurant. All rights reserved.
              </p>
              <p className="text-xs opacity-60 mt-1">
                Made with ❤️ for food lovers everywhere
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default MenuCatalog;